<!-- @format -->
<template>
    <div class="notification-view-widget" @click.stop>
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Notifications</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>

            <div
                :class="[
                    glassHiveUpdate.IsCallToActionEnabled ? '' : 'full-screen-height',
                    seeMoreView ? 'padding-bottom' : '',
                    'content-wrapper',
                ]"
            >
                <div class="view-bar">
                    <div v-for="view in views" :key="view.id">
                        <notification-view-tab
                            v-if="view.enable"
                            class="notification-view-tab"
                            :view="view"
                            :disabled="tabsDisabled"
                            :active="isActive(view)"
                            @click.native="setActive(view)"
                        />
                    </div>
                </div>
                <div v-if="!seeMoreView" class="actions-row">
                    <app-button v-if="isClientUser" class="settings-btn" @click="goToSettings">
                        Settings <s6-settings-outline-svg color="#18aeff" />
                    </app-button>
                    <app-button class="check-btn" @click="markAllAsRead">
                        Mark all as read <r3-checkmark-svg />
                    </app-button>
                </div>
                <loading-screen-svg v-if="loading" />

                <notification-container
                    v-if="!loading && !seeMoreView"
                    :notifications="notifications"
                    :current-tab-id="currentTabId"
                    @delete-notification="deleteNotification"
                    @mark-as-read="markAsReadNotification"
                />

                <update-section
                    v-if="seeMoreView"
                    back-label="Back to GlassHive Updates"
                    :date="glassHiveUpdate.UpdatedAt"
                    :body="glassHiveUpdate.Body"
                    :title="glassHiveUpdate.Title"
                    :url-image="glassHiveUpdate.Image"
                    @click-button="notificationsView"
                />
            </div>
            <image-loader v-if="hasScrolledToBottom && !seeMoreView" class="loading-step" />

            <div v-if="glassHiveUpdate.IsCallToActionEnabled" class="widget-footer">
                <app-button class="learn-more-btn" @click="endWidget()">
                    {{ glassHiveUpdate.CallToActionText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const NotificationViewTab = () => import('@/components/common/app-tab');
const ImageLoader = () => import('@/components/svg/image-loader');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const S6SettingsOutlineSvg = () => import('@/components/svg/grid/s6-settings-outline-svg');
const R3CheckmarkSvg = () => import('@/components/svg/grid/r3-checkmark-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const NotificationContainer = () => import('@/components/notification/notification-container');
const updateSection = () => import('@/components/common/update-section');

import EventBus from '@/scripts/event-bus.js';
import { NotificationWidgetService } from '@/scripts/services/notification-widget/NotificationWidgetService';

export default {
    name: 'notification-view-widget',

    components: {
        'app-button': AppButton,
        'notification-view-tab': NotificationViewTab,
        'notification-container': NotificationContainer,
        's6-settings-outline-svg': S6SettingsOutlineSvg,
        'r3-checkmark-svg': R3CheckmarkSvg,
        'widget-close-svg': WidgetCloseSvg,
        'image-loader': ImageLoader,
        'loading-screen-svg': LoadingScreenSvg,
        'update-section': updateSection,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            hasScrolledToBottom: false,

            currentTabId: 0,

            loading: true,

            limit: 20,

            offset: 0,

            totalNotificationNumber: 0,

            seeMoreView: false,

            glassHiveUpdate: [],

            tabsDisabled: false,

            notifications: [],

            scroll: false,

            views: [
                {
                    id: 0,
                    name: 'MY NOTIFICATIONS',
                    type: 'mine',
                    disabled: false,
                    enable: true,
                    content: [],
                },
                {
                    id: 1,
                    name: 'SALES TEAM',
                    type: 'sales',
                    disabled: false,
                    enable: true,
                    content: [],
                },
                {
                    id: 2,
                    name: 'GLASSHIVE UPDATES',
                    type: 'updates',
                    disabled: false,
                    enable: true,
                    content: [],
                },
            ],
        };
    },

    computed: {
        isClientUser() {
            return this.$store.getters.isClientUser;
        },

        isAgencyUser() {
            return this.$store.getters.isAgencyUser;
        },

        isOrganizationUser() {
            return this.$store.getters.isOrganizationUser;
        },

        isMasterAgencyUser() {
            return this.$store.getters.isMasterAgencyUser;
        },
    },

    watch: {
        '$store.state.notifications.newNotification': function () {
            this.pushNewNotification(this.$store.state.notifications.newNotification);
        },
    },

    created() {
        if (this.isClientUser) {
            this.currentTabId = 0;
        }
        if (this.isAgencyUser) {
            this.views[1].enable = false;
            this.currentTabId = 0;
        } else if (this.isMasterAgencyUser || this.isOrganizationUser) {
            this.views[0].enable = false;
            this.views[1].enable = false;
            this.currentTabId = 2;
        }

        EventBus.$on('handle-scroll', this.handleScroll);
        EventBus.$on('close-notifications-widget', this.closeWidget);
        EventBus.$on('notification-see-more-view', this.showMoreView);
    },

    beforeDestroy() {
        EventBus.$off('handle-scroll', this.handleScroll);
        EventBus.$off('close-notifications-widget', this.closeWidget);
        EventBus.$off('notification-see-more-view', this.showMoreView);
    },

    mounted() {
        this.getNotifications();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getNotificationSource(url) {
            if (url.includes('sales')) {
                return 'sales';
            } else if (url.includes('updates')) {
                return 'updates';
            } else {
                return 'mine';
            }
        },

        getNotifications() {
            this.offset === 0 ? (this.loading = true) : (this.hasScrolledToBottom = true);
            this.scroll = true;
            this.views[this.currentTabId].disabled = true;

            NotificationWidgetService.get(
                this.views[this.currentTabId].type,
                this.offset,
                this.limit
            )
                .then((response) => {
                    const url = response.request.responseURL;
                    const notifications = response.data.data;
                    const source = this.getNotificationSource(url);
                    const sourceId = this.views.findIndex((view) => view.type === source);

                    if (source === this.views[this.currentTabId].type) {
                        this.totalNotificationNumber += response.data.count;
                        this.views[this.currentTabId].content = [
                            ...this.views[this.currentTabId].content,
                            ...notifications,
                        ];
                        this.notifications = this.views[this.currentTabId].content;
                    }

                    this.views[sourceId].disabled = false;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching notifications',
                        { error },
                    );
                })
                .finally(() => {
                    this.offset += this.limit;
                    this.scroll = false;
                    this.loading = false;
                    this.hasScrolledToBottom = false;
                });
        },

        deleteNotification(notification, id) {
            NotificationWidgetService.delete(this.views[this.currentTabId].type, id)
                .then(() => {
                    this.notifications.splice(this.notifications.indexOf(notification), 1);
                    this.$store.dispatch('getBellCount');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while deleting a notification',
                        { error },
                    );
                });
        },

        markAllAsRead() {
            NotificationWidgetService.post(this.views[this.currentTabId].type)
                .then(() => {
                    this.notifications.forEach((notification) => (notification.Read = true));
                    this.$store.dispatch('getBellCount');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while while marking notifications as read',
                        { error },
                    );
                });
        },

        markAsReadNotification(id) {
            NotificationWidgetService.put(this.views[this.currentTabId].type, id, { Read: true })
                .then(() => {
                    this.$store.dispatch('getBellCount');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while marking notification as read',
                        { error },
                    );
                });
        },

        isActive(view) {
            return this.currentTabId === view.id;
        },

        setActive(view) {
            this.refresh();
            this.currentTabId = view.id;
            if (!view.disabled) {
                this.getNotifications();
            }
            this.views[this.currentTabId].content = [];
        },

        async handleScroll(el) {
            if (this.offset <= this.totalNotificationNumber && !this.seeMoreView && !this.scroll) {
                if (
                    el.srcElement.offsetHeight + el.srcElement.scrollTop >=
                    el.srcElement.scrollHeight - 50
                ) {
                    this.hasScrolledToBottom = true;
                    await this.getNotifications();
                }
            }
        },

        refresh() {
            this.offset = 0;
            this.totalNotificationNumber = 0;
        },

        showMoreView(notification) {
            this.tabsDisabled = true;
            this.glassHiveUpdate = notification;
            this.seeMoreView = true;
        },

        notificationsView() {
            this.tabsDisabled = false;
            this.seeMoreView = false;
            this.glassHiveUpdate = [];
        },

        pushNewNotification(notification) {
            const parseNotification = JSON.parse(notification);

            if (parseNotification.category) {
                const newNotification = {
                    category: parseNotification.category,
                    Data: parseNotification,
                    NotificationId: parseNotification.notificationId,
                    CreatedAt: parseNotification.CreatedAt,
                    Read: false,
                };
                this.views[this.currentTabId].content = [
                    newNotification,
                    ...this.views[this.currentTabId].content,
                ];
            } else {
                this.views[this.currentTabId].content = [
                    parseNotification,
                    ...this.views[this.currentTabId].content,
                ];
            }

            this.notifications = this.views[this.currentTabId].content;
            this.offset++;
            this.totalNotificationNumber++;
        },

        endWidget() {
            var cta = this.addHttpToUrl(this.glassHiveUpdate.CallToActionLink);

            window.open(cta, '_blank');
        },

        addHttpToUrl(url) {
            if (url && !url.includes('http://') && !url.includes('https://')) {
                return `http://${url}`;
            }

            return url;
        },

        goToSettings() {
            this.$router.push('/Profile/Personal#Notifications');
            this.closeWidget();
        },
    },
};
</script>

<style>
.notification-view-widget.widget {
    overflow-y: hidden;
    right: initial;
    left: 56px;
    border-top-right-radius: 19px;
    border-bottom-right-radius: 19px;
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
}

.notification-view-widget.widget .widget-footer {
    padding: 0 35px;
}

.notification-view-widget.widget.slide-enter-active,
.notification-view-widget.widget.slide-leave-active {
    transition: left 0.3s;
}

.notification-view-widget.widget.slide-enter,
.notification-view-widget.widget.slide-leave-to {
    left: -700px;
}

.notification-view-widget.widget .widget-header {
    border-top-left-radius: 0px;
}

.popup-backdrop {
    left: 0px;
}

.view-bar {
    border-bottom: 2px solid #e9e9ef;
    display: flex;
    margin: 0 24px;
}

.notification-view-widget .notification-view-tab {
    margin-right: 50px;
}

.notification-view-widget .widget-contents-container .full-screen-height {
    height: 100vh;
    padding: 8px 0 70px 0;
}

.notification-view-widget .widget-contents-container .padding-bottom {
    padding: 8px 0 0 0;
}

.widget-contents-container .loading-screen {
    margin-left: 250px;
}

.notification-view-widget .widget-contents-container .content-wrapper {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.notification-view-widget .widget-contents-container .notification-container {
    overflow-y: auto;
}

.actions-row {
    border-bottom: 2px solid #e9e9ef;
    display: flex;
    justify-content: space-between;
    padding: 7px 24px;
}

.notification-view-widget .widget-contents-container .content-wrapper .actions-row .settings-btn {
    display: flex;
    max-width: 128px;
    min-height: 40px;
}

.notification-view-widget .widget-contents-container .content-wrapper .actions-row .settings-btn {
    background-color: #ffffff;
    border: 1px solid #18aeff;
    color: #18aeff;
}

.notification-view-widget .widget-contents-container .content-wrapper .actions-row svg {
    margin-left: 10px;
}

.notification-view-widget
    .widget-contents-container
    .content-wrapper
    .actions-row
    .settings-btn
    svg {
    width: 15px;
}

.notification-view-widget .widget-contents-container .content-wrapper .actions-row .check-btn svg {
    height: 10.8px;
    width: 12.67px;
}

.notification-view-widget
    .widget-contents-container
    .content-wrapper
    .actions-row
    .check-btn
    svg
    path {
    fill: #18aeff;
    stroke: #ffffff;
}

.notification-view-widget .widget-footer .learn-more-btn {
    width: 100%;
}

.notification-view-widget .widget-contents-container .notification-container::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}

.notification-view-widget
    .widget-contents-container
    .notification-container::-webkit-scrollbar-track {
    border-radius: 32px;
}

.notification-view-widget
    .widget-contents-container
    .notification-container::-webkit-scrollbar-thumb {
    border-radius: 32px;
    background: rgb(193, 193, 197);
}

.notification-view-widget .widget-contents-container .content-wrapper .update-section {
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100%;
}

.notification-view-widget
    .widget-contents-container
    .content-wrapper
    .update-section
    .content-section {
    overflow-y: auto;
    height: 100%;
    padding: 15px 25px 80px 25px;
}

.notification-view-widget
    .widget-contents-container
    .content-wrapper
    .update-section
    .content-section::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}

.notification-view-widget
    .widget-contents-container
    .content-wrapper
    .update-section
    .content-section::-webkit-scrollbar-track {
    border-radius: 32px;
}

.notification-view-widget
    .widget-contents-container
    .content-wrapper
    .update-section
    .content-section::-webkit-scrollbar-thumb {
    border-radius: 32px;
    background: rgb(193, 193, 197);
}

@media screen and (max-width: 720px) {
    .notification-view-widget.widget {
        overflow-y: hidden;
        left: initial;
        right: 0;
        border-top-right-radius: 0px;
        border-bottom-right-radius: 0px;
        border-top-left-radius: 19px;
        border-bottom-left-radius: 0px;
    }
    .notification-view-widget.widget.slide-enter-active,
    .notification-view-widget.widget.slide-leave-active {
        transition: right 0.3s;
    }
    .notification-view-widget.widget-slide-enter,
    .notification-view-widget.widget-slide-leave-to {
        right: -700px;
    }
}
</style>
